import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Collapse,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import EventIcon from "@material-ui/icons/Event";
import EventAvailableOutlinedIcon from "@material-ui/icons/EventAvailableOutlined";
import BlockOutlinedIcon from "@material-ui/icons/BlockOutlined";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import * as api from "../../api/index";
import { useSnackbar } from "../../utils/useSnackbar";
import { CardSkeleton } from "../Skeletons";
import { useSubscription } from "../../Context/SubscriptionContext";
import { useTimezone } from "../../Context/TimezoneContext";
import ConfirmationDialog from "../ConfirmationDialog/ConfirmationDialog";

// Visual scale only (not a literal fraction of anything) - caps the gauge
// ring at 30 days out so "Active" deployments read as comfortably full
// rather than needing some arbitrary "total subscription length" concept
// this app doesn't otherwise track.
const GAUGE_CAP_DAYS = 30;

const QUICK_OPTIONS = [
  { value: "1", label: "1 Day" },
  { value: "2", label: "2 Days" },
  { value: "3", label: "3 Days" },
  { value: "5", label: "5 Days" },
  { value: "7", label: "1 Week" },
  { value: "30", label: "1 Month" },
];

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d;
};

// Month names, never numeric - "5/9/2026" is 5 Sep to half the world and
// 9 May to the other half, and this screen decides who gets locked out.
// `timeZone` is optional - passed as the SuperAdmin's selected display
// timezone (Context/TimezoneContext.js) everywhere this is called below, so
// every date on this tab is shown consistently in the zone they picked
// rather than whatever zone their browser/OS happens to be in.
const fmtDate = (d, timeZone) =>
  d.toLocaleDateString(undefined, { timeZone: timeZone || undefined, day: "numeric", month: "short", year: "numeric" });

const fmtShort = (d, timeZone) => d.toLocaleDateString(undefined, { timeZone: timeZone || undefined, day: "numeric", month: "short" });

const useStyles = makeStyles((theme) => ({
  wrap: {
    maxWidth: 900,
    margin: "0 auto",
  },
  // Stacked (was a cramped icon-beside-text row that left a lot of dead space
   // under the ring) - status pill, ring, then the facts, with an audit line
   // pinned to the bottom edge.
  hero: {
    position: "relative",
    overflow: "hidden",
    borderRadius: 20,
    padding: theme.spacing(3),
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    height: "100%",
    boxSizing: "border-box",
    textAlign: "center",
  },
  // Soft light source in the top-left, so the panel reads as a surface
  // rather than a flat rectangle of colour.
  heroGlow: {
    position: "absolute",
    top: -70,
    left: -50,
    width: 220,
    height: 220,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  heroActive: { background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)" },
  heroWarning: { background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" },
  heroExpired: { background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
  heroNone: { background: "linear-gradient(135deg, #64748b 0%, #475569 100%)" },
  heroPill: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "5px 13px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.3)",
    fontSize: "0.68rem",
    fontWeight: 800,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
  },
  heroPillDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#ffffff",
  },
  ringWrap: {
    position: "relative",
    width: 132,
    height: 132,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  ringTrack: {
    position: "absolute",
    color: "rgba(255,255,255,0.22)",
  },
  ringValue: {
    position: "absolute",
    color: "#ffffff",
  },
  ringLabel: {
    fontWeight: 800,
    fontSize: "2.3rem",
    lineHeight: 1,
    letterSpacing: "-0.02em",
  },
  ringUnit: {
    fontSize: "0.66rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    opacity: 0.85,
    marginTop: 3,
  },
  heroTitle: {
    position: "relative",
    fontWeight: 700,
    opacity: 0.85,
    fontSize: "0.68rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  heroStatus: {
    position: "relative",
    fontWeight: 800,
    fontSize: "1.25rem",
    lineHeight: 1.2,
  },
  heroDate: {
    position: "relative",
    opacity: 0.92,
    fontSize: "0.85rem",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginTop: 2,
  },
  // Who last touched this and when - the model already records it
  // (updatedBy/updatedAt on SubscriptionModel.js) and it was going unused,
  // which is exactly the thing you want after an unexpected lockout.
  heroFooter: {
    position: "relative",
    marginTop: "auto",
    paddingTop: 14,
    width: "100%",
    borderTop: "1px solid rgba(255,255,255,0.22)",
    fontSize: "0.7rem",
    opacity: 0.85,
    lineHeight: 1.45,
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    border: "1px solid #eceef1",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    padding: theme.spacing(3),
    height: "100%",
    boxSizing: "border-box",
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 700,
    color: "#1f2430",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  // Was a row of plain outlined chips showing only "1 Week" etc. Each option
  // now carries the date it would actually produce, so the consequence is
  // legible before selecting rather than only afterwards in the preview row.
  optionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(104px, 1fr))",
    gap: 10,
    marginBottom: theme.spacing(2),
  },
  optionCard: {
    border: "1.5px solid #e2e8f0",
    borderRadius: 12,
    padding: "10px 8px",
    background: "#ffffff",
    cursor: "pointer",
    textAlign: "center",
    transition: "border-color 0.15s ease, background-color 0.15s ease, transform 0.15s ease",
    "&:hover": {
      borderColor: "#fdba74",
      background: "#fffaf5",
      transform: "translateY(-1px)",
    },
  },
  optionCardActive: {
    borderColor: "transparent",
    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    boxShadow: "0 8px 16px -10px rgba(234, 88, 12, 0.9)",
    "&:hover": {
      background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      borderColor: "transparent",
    },
  },
  optionLabel: {
    fontWeight: 800,
    fontSize: "0.84rem",
    color: "#1f2430",
    lineHeight: 1.2,
  },
  optionLabelActive: {
    color: "#ffffff",
  },
  optionDate: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "#94a3b8",
    marginTop: 2,
  },
  optionDateActive: {
    color: "rgba(255,255,255,0.9)",
  },
  actionRow: {
    display: "flex",
    gap: 10,
    marginTop: theme.spacing(1),
    flexWrap: "wrap",
  },
  saveButton: {
    flex: 1,
    minWidth: 160,
    background: "linear-gradient(135deg, #ef7f1a, #f3961f)",
    color: "#fff",
    fontWeight: 700,
    borderRadius: 12,
    padding: "12px 30px",
    boxShadow: "0 10px 20px -10px rgba(239, 127, 26, 0.6)",
    textTransform: "none",
    // Without this the gradient above keeps rendering at full strength while
    // disabled, so "Select an option to save" still looked like a live button.
    "&.Mui-disabled": {
      background: "#f1f5f9",
      color: "#94a3b8",
      boxShadow: "none",
    },
  },
  // Clears the pending pick rather than navigating away - disabled when there
  // is nothing selected, so it never looks like it would undo the *saved*
  // expiry (it wouldn't; that's what Extend/Expire now are for).
  cancelButton: {
    flexShrink: 0,
    background: "#ffffff",
    color: "#ef7f1a",
    border: "1.5px solid #f0c898",
    borderRadius: 12,
    padding: "12px 26px",
    fontWeight: 700,
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      background: "#fff7ed",
      borderColor: "#ef7f1a",
    },
    "&.Mui-disabled": {
      background: "#ffffff",
      color: "#cbd5e1",
      borderColor: "#e2e8f0",
    },
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  previewRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid",
    fontSize: "0.82rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  // Kept visually distinct from (and below) Save so the two can never be
  // confused - this one revokes access for the whole deployment.
  dangerZone: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px dashed #e2e8f0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  dangerZoneTitle: {
    fontWeight: 700,
    color: "#b91c1c",
    fontSize: "0.86rem",
  },
  dangerZoneDesc: {
    color: "#64748b",
    fontSize: "0.76rem",
    lineHeight: 1.45,
    maxWidth: 330,
  },
  dangerBtn: {
    color: "#b91c1c",
    border: "1.5px solid #fecaca",
    background: "#fff5f5",
    borderRadius: 10,
    fontWeight: 700,
    textTransform: "none",
    padding: "7px 16px",
    flexShrink: 0,
    "&:hover": {
      background: "#fee2e2",
      borderColor: "#f87171",
    },
  },
}));

const SubscriptionTab = () => {
  const classes = useStyles();
  const [openSnackbar] = useSnackbar();
  // Shared with SuperAdminNavbar.js's chip and OverviewTab.js's stat card
  // (was: its own independent fetch) - see SubscriptionContext.js. setSubscription
  // lets a successful save update every consumer immediately.
  const { subscription, loading, setSubscription } = useSubscription();
  const { timezone } = useTimezone();
  const [saving, setSaving] = useState(false);
  // No default quick-pick (was: "1", pre-selected on every mount) - Save was
  // therefore clickable the instant this tab loaded, before the admin chose
  // anything at all, one misclick away from silently re-extending the
  // subscription to "1 Day" from now. Nothing is selected until they
  // deliberately pick one, and Save stays disabled until they do.
  const [selectedOption, setSelectedOption] = useState("");
  const [customDate, setCustomDate] = useState(null);
  const [confirmShorten, setConfirmShorten] = useState(false);
  const [confirmExpireNow, setConfirmExpireNow] = useState(false);
  const [expiring, setExpiring] = useState(false);
  // "Last changed 2 minutes ago" is computed at render time, so without a
  // periodic nudge it freezes at whatever it said when the panel mounted and
  // only looks right again after a reload.
  const [, setRelativeTimeTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRelativeTimeTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const currentExpiry = subscription?.subscriptionExpiry ? new Date(subscription.subscriptionExpiry) : null;
  const alreadyExpired = !!(currentExpiry && currentExpiry.getTime() <= Date.now());

  // Custom Date's minimum selectable day - was disablePast, which still let
  // an admin pick *today*. That's what produced the exact result in the
  // screenshot that prompted this: a "Custom Date" save of today's date
  // instantly set the subscription to "Expires in 0d" instead of extending
  // it. Excluding today too closes that specific trap.
  const minCustomDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  const handleQuickPick = (value) => {
    setSelectedOption(value);
    if (value !== "custom") setCustomDate(null);
  };

  // Discards the pending pick and puts the panel back in its safe
  // nothing-selected state (which also collapses the custom date field and
  // re-disables Save). Does not touch the saved expiry.
  const handleCancelSelection = () => {
    setSelectedOption("");
    setCustomDate(null);
  };

  const calculateExpiryDate = () => {
    if (!selectedOption) return null;
    if (selectedOption === "custom") {
      return customDate || null;
    }
    const days = parseInt(selectedOption, 10);
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + days);
    return expiry;
  };

  const canSave = selectedOption === "custom" ? !!customDate : !!selectedOption;

  // Live preview of what Save would actually do - shown under the picker so
  // the effect is visible *before* clicking Save, not discovered after (the
  // gauge card on the left only updates once the request round-trips).
  const previewExpiry = calculateExpiryDate();
  const previewDaysLeft = previewExpiry
    ? Math.ceil((previewExpiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isShortening = !!(previewExpiry && currentExpiry && previewExpiry.getTime() < currentExpiry.getTime());

  const performSave = async () => {
    const expiryDate = calculateExpiryDate();
    if (!expiryDate) {
      openSnackbar("Please select a date");
      return;
    }

    try {
      setSaving(true);
      const { data } = await api.updateSubscription({
        subscriptionType: selectedOption === "custom" ? "custom" : "days",
        subscriptionValue: selectedOption === "custom" ? 0 : parseInt(selectedOption, 10),
        subscriptionExpiry: expiryDate,
        isActive: true,
      });
      // Prefer the server's own document - it carries the authoritative
      // updatedAt/updatedBy, which a hand-rolled optimistic merge left stale,
      // so the "Last changed ..." line sat frozen until a page reload.
      // Updates SuperAdminNavbar's chip and OverviewTab's stat card in the
      // same tick either way, no second round trip needed.
      setSubscription((prev) =>
        data?.data
          ? { ...(prev || {}), ...data.data }
          : { ...(prev || {}), subscriptionExpiry: expiryDate, isActive: true, updatedAt: new Date().toISOString() }
      );
      openSnackbar("Subscription updated successfully");
      setSelectedOption("");
      setCustomDate(null);
    } catch (error) {
      console.error("Error:", error);
      openSnackbar(error?.response?.data?.message || "Failed to update subscription");
    } finally {
      setSaving(false);
      setConfirmShorten(false);
    }
  };

  // Save's onClick target - performSave does the actual write, this decides
  // whether it's safe to go straight there. A pick that would shorten (or,
  // for Custom Date, expire-today) the subscription instead of extending it
  // is exactly the kind of fat-finger mistake worth pausing on rather than
  // firing immediately.
  const handleSave = () => {
    if (!calculateExpiryDate()) {
      openSnackbar("Please select a date");
      return;
    }
    if (isShortening) {
      setConfirmShorten(true);
      return;
    }
    performSave();
  };

  // Deliberate kill switch, separate from the Extend flow above: sets the
  // expiry to right now so every store user is blocked at their next sign-in
  // (superAdmin bypasses the expiry check in middleware/expiryDate.js, so
  // this can't lock the person pressing it out of this panel). isActive stays
  // true so users get the branded "contact support" expiry message rather
  // than the blunter "subscription is not active" one.
  const handleExpireNow = async () => {
    try {
      setExpiring(true);
      const now = new Date();
      const { data } = await api.updateSubscription({
        subscriptionType: "custom",
        subscriptionValue: 0,
        subscriptionExpiry: now,
        isActive: true,
      });
      // Server document first, for the same updatedAt/updatedBy reason as in
      // performSave above.
      setSubscription((prev) =>
        data?.data
          ? { ...(prev || {}), ...data.data }
          : { ...(prev || {}), subscriptionExpiry: now, isActive: true, updatedAt: new Date().toISOString() }
      );
      openSnackbar("Subscription ended - store users are locked out from now", "warning");
      setSelectedOption("");
      setCustomDate(null);
    } catch (error) {
      openSnackbar(error?.response?.data?.message || "Failed to expire subscription");
    } finally {
      setExpiring(false);
      setConfirmExpireNow(false);
    }
  };

  const getStatus = () => {
    if (!currentExpiry) {
      return { label: "No expiry set", pill: "Unconfigured", tone: "none", daysLeft: null };
    }
    const daysLeft = Math.ceil((currentExpiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) {
      return { label: "Store users are locked out", pill: "Expired", tone: "expired", daysLeft };
    }
    if (daysLeft === 0) {
      return { label: "Expires today", pill: "Expiring", tone: "warning", daysLeft };
    }
    if (daysLeft <= 10) {
      return { label: "Renewal due soon", pill: "Expiring", tone: "warning", daysLeft };
    }
    return { label: "Licence active", pill: "Active", tone: "active", daysLeft };
  };

  if (loading) {
    return (
      <Box className={classes.wrap}>
        <CardSkeleton />
      </Box>
    );
  }

  const status = getStatus();
  const heroClass = {
    active: classes.heroActive,
    warning: classes.heroWarning,
    expired: classes.heroExpired,
    none: classes.heroNone,
  }[status.tone];
  const gaugeValue =
    status.daysLeft === null
      ? 0
      : Math.max(0, Math.min(100, (status.daysLeft / GAUGE_CAP_DAYS) * 100));

  return (
    <Box className={classes.wrap}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <div className={`${classes.hero} ${heroClass}`}>
            <span className={classes.heroGlow} aria-hidden="true" />

            <span className={classes.heroPill}>
              <span className={classes.heroPillDot} />
              {status.pill}
            </span>

            <div className={classes.ringWrap}>
              <CircularProgress
                variant="determinate"
                value={100}
                size={132}
                thickness={3.5}
                className={classes.ringTrack}
              />
              <CircularProgress
                variant="determinate"
                value={gaugeValue}
                size={132}
                thickness={3.5}
                className={classes.ringValue}
              />
              <span className={classes.ringLabel}>
                {status.daysLeft === null ? "–" : Math.abs(status.daysLeft)}
              </span>
              <span className={classes.ringUnit}>
                {status.daysLeft === null
                  ? "not set"
                  : status.daysLeft < 0
                  ? status.daysLeft === -1
                    ? "day ago"
                    : "days ago"
                  : status.daysLeft === 1
                  ? "day left"
                  : "days left"}
              </span>
            </div>

            <div>
              <div className={classes.heroTitle}>Deployment Subscription</div>
              <div className={classes.heroStatus}>{status.label}</div>
              {currentExpiry && (
                <div className={classes.heroDate}>
                  <EventAvailableOutlinedIcon fontSize="small" />
                  {/* Explicit month name - "5/9/2026" reads as 5 Sep to half
                      the world and 9 May to the other half. */}
                  Expires {fmtDate(currentExpiry, timezone)}
                </div>
              )}
            </div>

            <div className={classes.heroFooter}>
              {subscription?.updatedAt ? (
                <>
                  Last changed {moment(subscription.updatedAt).fromNow()}
                  {subscription.updatedBy ? ` by ${subscription.updatedBy}` : ""}
                </>
              ) : (
                "No changes recorded yet"
              )}
            </div>
          </div>
        </Grid>

        <Grid item xs={12} md={7}>
          <div className={classes.card}>
            <Typography variant="h6" className={classes.title}>
              <EventIcon style={{ color: "#ef7f1a" }} />
              Extend Subscription
            </Typography>

            <div className={classes.optionGrid}>
              {QUICK_OPTIONS.map((opt) => {
                const active = selectedOption === opt.value;
                return (
                  <div
                    key={opt.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleQuickPick(opt.value)}
                    onKeyDown={(e) => (e.key === "Enter" ? handleQuickPick(opt.value) : null)}
                    className={`${classes.optionCard} ${active ? classes.optionCardActive : ""}`}
                  >
                    <div className={`${classes.optionLabel} ${active ? classes.optionLabelActive : ""}`}>
                      {opt.label}
                    </div>
                    <div className={`${classes.optionDate} ${active ? classes.optionDateActive : ""}`}>
                      {fmtShort(addDays(parseInt(opt.value, 10)), timezone)}
                    </div>
                  </div>
                );
              })}
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleQuickPick("custom")}
                onKeyDown={(e) => (e.key === "Enter" ? handleQuickPick("custom") : null)}
                className={`${classes.optionCard} ${selectedOption === "custom" ? classes.optionCardActive : ""}`}
              >
                <div
                  className={`${classes.optionLabel} ${
                    selectedOption === "custom" ? classes.optionLabelActive : ""
                  }`}
                >
                  Custom
                </div>
                <div
                  className={`${classes.optionDate} ${
                    selectedOption === "custom" ? classes.optionDateActive : ""
                  }`}
                >
                  {customDate ? fmtShort(customDate, timezone) : "pick a date"}
                </div>
              </div>
            </div>

            <Collapse in={selectedOption === "custom"} timeout={220}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  fullWidth
                  disableToolbar
                  variant="inline"
                  inputVariant="outlined"
                  format="MMM dd, yyyy"
                  label="Select Date"
                  value={customDate}
                  onChange={(date) => setCustomDate(date)}
                  // Was disablePast, which still allowed *today* - picking
                  // today isn't an extension, it's an immediate "Expires in
                  // 0d". minDate excludes today too.
                  minDate={minCustomDate}
                  minDateMessage="Pick a date after today - that's not an extension otherwise."
                  className={classes.formField}
                  KeyboardButtonProps={{ "aria-label": "change date" }}
                />
              </MuiPickersUtilsProvider>
            </Collapse>

            {/* Live preview of what Save would do - visible before clicking,
                not just discovered after via the gauge card updating. */}
            {previewExpiry && (
              <div
                className={classes.previewRow}
                style={{
                  borderColor: isShortening ? "#fecaca" : "#bbf7d0",
                  backgroundColor: isShortening ? "#fef2f2" : "#f0fdf4",
                  color: isShortening ? "#b91c1c" : "#15803d",
                }}
              >
                <EventAvailableOutlinedIcon fontSize="small" />
                <span>
                  {isShortening ? "This shortens the subscription to " : "New expiry: "}
                  <strong>
                    {fmtDate(previewExpiry, timezone)}
                  </strong>{" "}
                  ({previewDaysLeft}d from now)
                </span>
              </div>
            )}

            <div className={classes.actionRow}>
              <Button
                className={classes.cancelButton}
                disableElevation
                onClick={handleCancelSelection}
                disabled={saving || !selectedOption}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className={classes.saveButton}
                disableElevation
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !canSave}
              >
                {saving ? "Saving..." : canSave ? "Save" : "Select an option to save"}
              </Button>
            </div>

            <div className={classes.dangerZone}>
              <div>
                <div className={classes.dangerZoneTitle}>Expire now</div>
                <div className={classes.dangerZoneDesc}>
                  Ends the licence immediately. Store users are locked out at their next
                  sign-in; your own SuperAdmin access is unaffected.
                </div>
              </div>
              <Button
                className={classes.dangerBtn}
                startIcon={<BlockOutlinedIcon fontSize="small" />}
                onClick={() => setConfirmExpireNow(true)}
                disabled={saving || expiring || !currentExpiry || alreadyExpired}
                disableElevation
              >
                {expiring ? "Ending..." : alreadyExpired ? "Already expired" : "Expire now"}
              </Button>
            </div>
          </div>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={confirmShorten}
        title="This shortens the subscription"
        message={
          previewExpiry
            ? `The current expiry is ${currentExpiry ? fmtDate(currentExpiry, timezone) : "-"}. This change sets it to ${fmtDate(previewExpiry, timezone)} (${previewDaysLeft}d from now) instead - earlier than it already is. Continue anyway?`
            : "This change moves the expiry earlier than it already is. Continue anyway?"
        }
        confirmLabel={saving ? "Saving..." : "Yes, shorten it"}
        onCancel={() => setConfirmShorten(false)}
        onConfirm={performSave}
      />

      <ConfirmationDialog
        open={confirmExpireNow}
        title="End the subscription now?"
        message={
          `Every user of this deployment will be locked out the next time they sign in, and ` +
          `will see the "contact support" screen instead. Your own SuperAdmin access is not ` +
          `affected, and you can extend the licence again at any time from this panel.` +
          (currentExpiry
            ? ` The current expiry (${fmtDate(currentExpiry, timezone)}) will be discarded.`
            : "")
        }
        confirmLabel={expiring ? "Ending..." : "Yes, expire now"}
        onCancel={() => setConfirmExpireNow(false)}
        onConfirm={handleExpireNow}
      />
    </Box>
  );
};

export default SubscriptionTab;
